import React, { useState } from 'react';
import { Building2, Users, Trophy, BarChart3 } from 'lucide-react';
import { useCompanies } from '../../hooks/useCompanies';
import { useCustomers } from '../../hooks/useCustomers';
import { usePoints } from '../../hooks/usePoints';
import LoadingSpinner from '../common/LoadingSpinner';

const AdminPointsView = () => {
  const { companies, loading: companiesLoading } = useCompanies();
  const { customers, loading: customersLoading } = useCustomers();
  const { pointsHistory, loading: pointsLoading } = usePoints();

  const loading = companiesLoading || customersLoading || pointsLoading;

  if (loading) {
    return <LoadingSpinner size="lg" className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Pontos das Empresas</h2>
      
      <div className="grid gap-6">
        {companies.map(company => {
          const companyCustomers = customers.filter(c => c.companyId === company.id);
          const totalPoints = companyCustomers.reduce((sum, c) => sum + (c.points || 0), 0);
          const companyHistory = pointsHistory.filter(h => {
            const customer = customers.find(c => c.id === h.customerId);
            return customer?.companyId === company.id;
          });
          
          return (
            <div key={company.id} className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{company.name}</h3>
                  <p className="text-sm text-gray-600">{company.email}</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs mt-1 ${
                    company.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {company.active ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{totalPoints}</p>
                  <p className="text-sm text-gray-600">Total de Pontos</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-lg font-bold text-blue-600">{companyCustomers.length}</p>
                  <p className="text-xs text-gray-600">Clientes</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Trophy className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-lg font-bold text-green-600">{totalPoints}</p>
                  <p className="text-xs text-gray-600">Pontos Totais</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <BarChart3 className="w-5 h-5 text-yellow-600" />
                  </div>
                  <p className="text-lg font-bold text-yellow-600">
                    {companyCustomers.length > 0 ? Math.round(totalPoints / companyCustomers.length) : 0}
                  </p>
                  <p className="text-xs text-gray-600">Média</p>
                </div>
              </div>

              {companyCustomers.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Top 3 Clientes</h4>
                  <div className="space-y-2">
                    {companyCustomers
                      .sort((a, b) => (b.points || 0) - (a.points || 0))
                      .slice(0, 3)
                      .map((customer, index) => (
                        <div key={customer.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                            <span className="text-sm text-gray-700">{customer.name}</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{customer.points || 0} pts</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {companyCustomers.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm">Nenhum cliente cadastrado</p>
                </div>
              )}
            </div>
          );
        })}
        
        {companies.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p>Nenhuma empresa cadastrada ainda</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPointsView;